import React, { useEffect, useState } from "react";
import OPPCss from "../../Dashboard/Opportunities/Opportunities.module.scss";
import SidebarFacility from "../SidebarFacility";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader/Loader";
import LocationDropdown from "../../Dashboard/VenueListComp/LocationDropdown";
import "../../Dashboard/VenueListComp/VenueList.scss";
import { Edit } from "lucide-react";

// Modal Component for Editing Blocked Slot
const EditBlockedSlotModal = ({ isOpen, onClose, onSubmit, slotData }) => {
  const [duration, setDuration] = useState(slotData?.duration_hrs || "");
  const [courtsReq, setCourtsReq] = useState(slotData?.courts_req || "");
  const [error, setError] = useState(null);

  useEffect(() => {
    // Update form fields when slotData changes
    setDuration(slotData?.duration_hrs || "");
    setCourtsReq(slotData?.courts_req || "");
    setError(null);
  }, [slotData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!duration || !courtsReq) {
      setError("Please fill in all fields");
      return;
    }
    if (isNaN(duration) || duration <= 0) {
      setError("Duration must be a positive number");
      return;
    }
    if (
      isNaN(courtsReq) ||
      courtsReq <= 0 ||
      !Number.isInteger(Number(courtsReq))
    ) {
      setError("Courts required must be a positive integer");
      return;
    }

    try {
      await onSubmit({ duration, courtsReq });
      onClose();
    } catch (err) {
      setError(err.message);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modalOverlayFaclitityAllBooking">
      <div className="modalContentFaclitityAllBooking">
        <h3>Edit Blocked Slot</h3>
        <p>ID: {slotData?.id}</p>
        <form onSubmit={handleSubmit}>
          <div className="formGroupFaclitityAllBooking">
            <label htmlFor="durationFaclitityAllBooking">
              Duration (Hours):
            </label>
            <input
              type="number"
              id="duration"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              step="0.5"
              min="0.5"
            />
          </div>
          <div className="formGroupFaclitityAllBooking">
            <label htmlFor="courtsReqFaclitityAllBooking">
              Courts Required:
            </label>
            <input
              type="number"
              id="courtsReq"
              value={courtsReq}
              onChange={(e) => setCourtsReq(e.target.value)}
              min="1"
              step="1"
            />
          </div>
          {error && <p className="errorFaclitityAllBooking">{error}</p>}
          <div className="modalButtonsFaclitityAllBooking">
            <button type="submit" className="confirmButtonFaclitityAllBooking">
              Save
            </button>
            <button
              type="button"
              onClick={onClose}
              className="cancelButtonFaclitityAllBooking"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

function FacilityAllBookings() {
  const navigate = useNavigate();
  const [hideSvg, setHideSvg] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const [timeFrame, setTimeFrame] = useState("Daily");
  const [status, setStatus] = useState("All");
  const [view, setView] = useState("bookings"); // 'bookings' or 'blocked_slots'
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [statusChanged, setStatusChanged] = useState(false);

  const handleSvg = () => {
    setHideSvg(!hideSvg);
  };

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem("access_token");
    const timeFrameValue = timeFrame.toLowerCase();
    let url = "";

    if (view === "bookings") {
      url = `https://ballersapi.ballerslife.co/api/v1/facility_admin/bookings_overview?time_frame=${timeFrameValue}`;
      if (statusChanged && status !== "All") {
        url += `&status=${status.toLowerCase()}`;
      }
    } else if (view === "blocked_slots") {
      url = `https://ballersapi.ballerslife.co/api/v1/facility_admin/blocked_slots_overview?time_frame=${timeFrameValue}`;
    }

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();
      const dataArray =
        view === "bookings" ? data.bookings : data.blocked_slots || [];
      setBookings(dataArray);
      setNoData(dataArray.length === 0);
      setLoading(false);
    } catch (err) {
      setNoData(true);
      setLoading(false);
    }
  };

  const handleEditSlot = async ({ duration, courtsReq }) => {
    const token = localStorage.getItem("access_token");
    const url = `https://ballersapi.ballerslife.co/api/v1/facility_admin/edit_blocked_slot/${selectedSlot.id}?duration=${duration}&court_req=${courtsReq}`;

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to update blocked slot");
      }

      // Update the bookings state directly with the new values
      setBookings((prevBookings) =>
        prevBookings.map((slot) =>
          slot.id === selectedSlot.id
            ? {
                ...slot,
                duration_hrs: parseFloat(duration),
                courts_req: parseInt(courtsReq, 10),
              }
            : slot
        )
      );
    } catch (err) {
      throw new Error(err.message);
    }
  };

  const openEditModal = (slot) => {
    setSelectedSlot(slot);
    setIsModalOpen(true);
  };

  useEffect(() => {
    fetchData();
  }, [timeFrame, status, view]);

  useEffect(() => {
    document.title = `All Bookings | Ballers`;
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    setStatusChanged(false);
    setStatus("All");
  }, [view, timeFrame]);

  return (
    <div className="dashMain">
      <div className="sideBar">
        <SidebarFacility />
      </div>
      {hideSvg ? (
        <div className="sideBarMobile">
          <SidebarFacility />
        </div>
      ) : (
        <div></div>
      )}
      <div className="content">
        <>
          <div className="kycOptNew" style={{ marginTop: "3.5rem" }}>
            <p
              onClick={() => setView("bookings")}
              style={{
                color: view === "bookings" ? "#ffbf00" : "white",
                cursor: "pointer",
              }}
            >
              Blocked By Ballers
            </p>
            <p
              onClick={() => setView("blocked_slots")}
              style={{
                color: view === "blocked_slots" ? "#ffbf00" : "white",
                cursor: "pointer",
              }}
            >
              Blocked Slot
            </p>
          </div>
          <div className="filtersAllBooking" style={{ marginTop: "2rem" }}>
            {view === "bookings" && (
              <>
                <div className="dropdownAllBooking">
                  <label htmlFor="timeframe-select">Time Frame:</label>
                  <LocationDropdown
                    location={timeFrame}
                    setLocation={setTimeFrame}
                    locationOptions={["Daily", "Weekly", "Monthly", "Yearly"]}
                  />
                </div>
                <div className="dropdownAllBooking">
                  <label htmlFor="status-select">Status:</label>
                  <LocationDropdown
                    location={status}
                    setLocation={(val) => {
                      setStatus(val);
                      setStatusChanged(true);
                    }}
                    locationOptions={[
                      "All",
                      "Confirmed",
                      "Completed",
                      "Cancelled",
                    ]}
                  />
                </div>
              </>
            )}
            {view === "blocked_slots" && (
              <div className="dropdownAllBooking">
                <label htmlFor="timeframe-select">Time Frame:</label>
                <LocationDropdown
                  location={timeFrame}
                  setLocation={setTimeFrame}
                  locationOptions={["Daily", "Weekly", "Monthly", "Yearly"]}
                />
              </div>
            )}
          </div>

          {loading ? (
            <Loader />
          ) : (
            <>
              {noData ? (
                <div className={OPPCss.fcAllBookingMain}>
                  No {view === "bookings" ? "bookings" : "blocked slots"}{" "}
                  available.
                </div>
              ) : (
                <>
                  <div className={OPPCss.fcAllBookingMain}>
                    <div className={OPPCss.fcAllBookingHeader}>
                      {view === "bookings" ? (
                        <div className={OPPCss.fcAllBookingCell}>
                          <p className="investmentHeader">USER</p>
                        </div>
                      ) : (
                        <></>
                      )}
                      <div className={OPPCss.fcAllBookingCell}>
                        <p className="investmentHeader">SPORT</p>
                      </div>
                      <div className={OPPCss.fcAllBookingCell}>
                        <p className="investmentHeader">DATE</p>
                      </div>
                      {view === "bookings" ? (
                        <div className={OPPCss.fcAllBookingCell}>
                          <p className="investmentHeader">TIME</p>
                        </div>
                      ) : (
                        <div className={OPPCss.fcAllBookingCell}>
                          <p className="investmentHeader">START TIME</p>
                        </div>
                      )}
                      {view === "bookings" ? (
                        <div className={OPPCss.fcAllBookingCell}>
                          <p className="investmentHeader">DURATION (HRS)</p>
                        </div>
                      ) : (
                        <div className={OPPCss.fcAllBookingCell}>
                          <p className="investmentHeader">END TIME</p>
                        </div>
                      )}
                      {view === "bookings" ? (
                        <div className={OPPCss.fcAllBookingCell}>
                          <p className="investmentHeader">COURTS</p>
                        </div>
                      ) : (
                        <div className={OPPCss.fcAllBookingCell}>
                          <p className="investmentHeader">DURATION (HRS)</p>
                        </div>
                      )}
                      {view === "bookings" ? (
                        <div className={OPPCss.fcAllBookingCell}>
                          <p className="investmentHeader">STATUS</p>
                        </div>
                      ) : (
                        <div className={OPPCss.fcAllBookingCell}>
                          <p className="investmentHeader">COURTS</p>
                        </div>
                      )}
                      {view === "blocked_slots" && (
                        <div className={OPPCss.fcAllBookingCell}>
                          <p className="investmentHeader">ACTION</p>
                        </div>
                      )}
                    </div>
                    {bookings.map((item, index) => (
                      <div
                        className={OPPCss.fcAllBookingRow}
                        key={item.id || index}
                      >
                        {view === "bookings" ? (
                          <div className={OPPCss.fcAllBookingCell}>
                            <p>{item.user || "N/A"}</p>
                          </div>
                        ) : (
                          <></>
                        )}
                        <div className={OPPCss.fcAllBookingCell}>
                          <p>{item.sport || "N/A"}</p>
                        </div>
                        <div className={OPPCss.fcAllBookingCell}>
                          <p>{item.date || "N/A"}</p>
                        </div>
                        <div className={OPPCss.fcAllBookingCell}>
                          <p>
                            {view === "bookings"
                              ? item.time || "N/A"
                              : item.time || "N/A"}
                          </p>
                        </div>
                        {view === "bookings" ? (
                          <div className={OPPCss.fcAllBookingCell}>
                            <p>{item.duration_hrs || "N/A"}</p>
                          </div>
                        ) : (
                          <div className={OPPCss.fcAllBookingCell}>
                            <p>{item.end_time || "N/A"}</p>
                          </div>
                        )}
                        {view === "bookings" ? (
                          <div className={OPPCss.fcAllBookingCell}>
                            <p>{item.courts_req || "N/A"}</p>
                          </div>
                        ) : (
                          <div className={OPPCss.fcAllBookingCell}>
                            <p>{item.duration_hrs || "N/A"}</p>
                          </div>
                        )}
                        {view === "bookings" ? (
                          <div className={OPPCss.fcAllBookingCell}>
                            <p>{item.status || "N/A"}</p>
                          </div>
                        ) : (
                          <div className={OPPCss.fcAllBookingCell}>
                            <p>{item.courts_req || "N/A"}</p>
                          </div>
                        )}
                        {view === "blocked_slots" && (
                          <>
                            {!item.is_expired && (
                              <div
                                className={OPPCss.fcAllBookingCell}
                                onClick={() => openEditModal(item)}
                              >
                                <p>
                                  <Edit />
                                </p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
          <EditBlockedSlotModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSubmit={handleEditSlot}
            slotData={selectedSlot}
          />
        </>
      </div>
    </div>
  );
}

export default FacilityAllBookings;
